<template>
  <v-col cols="12">
    <ValidationObserver ref="observer">
      <div class="mb-4 questionText d-flex align-start">
        <div
          class="mr-3"
          v-if="question.questionNumber && question.questionNumber.length > 0"
        >
          {{ question.questionNumber }}
        </div>
        <survey-question-text :text="question.text"></survey-question-text>
      </div>

      <ValidationProvider
        :rules="rules"
        v-slot="{ errors }"
        :vid="question.key"
      >
        <v-textarea
          ref="field"
          :label="label"
          :value="answer"
          @input="setAnswer"
          outlined
          :error-messages="errors"
          rows="3"
          :counter="errors.length === 0 ? maxLength : null"
          :maxlength="maxLength"
        >
        </v-textarea>
      </ValidationProvider>
    </ValidationObserver>
  </v-col>
</template>

<style lang="scss">
.questionText {
  font-size: 1.1rem;
}

.radio-button {
  padding-top: 0.75rem !important;
  padding-bottom: 0.75rem !important;
  height: auto !important;
  text-align: left;
  justify-content: start;
  white-space: normal;
  display: block;
  text-transform: none !important;
}
</style>

<script>
import Vue from "vue";
import {
  getQuestionSetting,
  questionSettings,
  showQuestion,
  requireQuestion,
  getQuestionHTML,
  getQuestionTextParts,
  questionTextPartType,
} from "../constants/Survey";
import SurveyQuestionText from "./Survey.Question.Text.vue";

export default {
  name: "SurveyQuestionTextField",
  components: {
    SurveyQuestionText,
  },
  data: () => ({
    innerValue: [],
    validated: false,
  }),
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    question: {
      type: Object,
    },
  },
  computed: {
    required() {
      return (
        getQuestionSetting(
          this.question.settings,
          questionSettings.required.key
        ).value == "true" || requireQuestion(this.question, this.innerValue, true)
      );
    },
    minLength() {
      return (
        getQuestionSetting(
          this.question.settings,
          questionSettings.minLength.key
        )?.value ?? 0
      );
    },
    maxLength() {
      return (
        getQuestionSetting(
          this.question.settings,
          questionSettings.maxLength.key
        )?.value ?? 2000
      );
    },
    label() {
      return `Answer${this.required ? "*" : ""}`;
    },
    rules() {
      const results = [];

      if (this.required) results.push("required");
      if (this.minLength) results.push(`min:${this.minLength}`);
      if (this.maxLength) results.push(`max:${this.maxLength}`);

      return results.join("|");
    },
    fieldValue() {
      let target = this.innerValue.find((y) => y.key === this.question.key);
      return target ? target.value : null;
    },
    visible() {
      return showQuestion(this.question, this.innerValue);
    },
    questionHTML() {
      return getQuestionHTML(this.question.text);
    },
    questionTextParts() {
      return getQuestionTextParts(this.question.text);
    },
    questionTextPartType() {
      return questionTextPartType;
    },
    answer() {
      return this.innerValue.find((x) => x.key === this.question.key)?.value;
    },
    isValid() {
      return this.errors.length === 0;
    },
    errors() {
      let result = [];
      let length = this.answer?.length ?? 0;

      if (this.required && length < 1) result.push("Required");
      else if (length) {
        if (length < this.minLength)
          result.push(`Must be at least ${this.minLength} characters.`);
        if (length > this.maxLength)
          result.push(`Cannot be more than ${this.maxLength} characters.`);
      }

      return result;
    },
  },
  watch: {
    value(val) {
      if (!keyPairArraysAreEqual(val, this.innerValue)) {
        this.innerValue = val;
      }
    },
    innerValue(value, prev) {
      if (!keyPairArraysAreEqual(value, prev)) {
        this.$emit("input", this.innerValue);
      }
    },
  },
  methods: {
    stopper(e) {
      if (e.code != "Backspace" && e.target.value.length > this.maxLength)
        e.target.value = e.target.value.slice(0, this.maxLength - 1);
    },
    setAnswer(value) {
      if (value?.length > this.maxLength - 1) {
        value = value.slice(0, this.maxLength - 1);
      }

      let keyPair = { key: this.question.key, value };
      let index = this.innerValue.findIndex((x) => x.key === this.question.key);
      if (index > -1) Vue.set(this.innerValue, index, keyPair);
      else this.innerValue.push(keyPair);
    },
    validate() {
      this.$refs.observer.validate();
      return this.isValid;
    },
    reset() {
      this.$refs.observer.reset();
    },
  },
  mounted() {
    if (this.value) this.innerValue = this.value;
  },
  destroyed() {
    // Clear answer if hidden.
    if (!showQuestion(this.question, this.innerValue)) this.setAnswer(null);
  },
};

function keyPairArraysAreEqual(a, b) {
  return flatten(a) === flatten(b);

  function flatten(arr) {
    return JSON.parse(JSON.stringify(arr));
  }
}
</script>
